import { useSite } from "@/context/SiteContext";

const g = (id, title, core, items) => ({ id, title, core, items: items.map(([key, def, where, max]) => ({ key, def, where, max })) });

export const WORDING_GROUPS = [
  g("header", "Header", false, [
    ["header.subtitle", "Chronicles & Codex", "Small line under the site name at the top"],
  ]),
  g("home", "Home page", false, [
    ["home.eyebrow", "An imprint of chronicles", "Small heading above the site title"],
    ["home.enter_codex", "Enter the Codex", "Main hero button"],
    ["home.wander", "Wander the Codex", "Hero random-page button"],
    ["home.realms", "The Realms", "Hero map button"],
    ["home.books_eyebrow", "The Published Chronicles", "Small heading above the books"],
    ["home.books_title", "Books of the Imprint", "Books heading"],
    ["home.archive_eyebrow", "The Codex of Elirios", "Small heading above the sections"],
    ["home.archive_title", "Chambers of the Archive", "Sections heading"],
    ["home.open", "Open", "Link on each section tile"],
    ["home.map_eyebrow", "Cartography", "Small heading above the map teaser"],
    ["home.map_cta", "Unroll the map", "Map teaser button"],
    ["home.recent_eyebrow", "Fresh ink", "Small heading above recent pages"],
    ["home.recent_title", "Recently Inscribed", "Recent pages heading"],
  ]),
  g("books", "Books & series", false, [
    ["book.read_kindle", "Read on Kindle", "Under a book cover with a Kindle link"],
    ["book.forthcoming", "Kindle link forthcoming", "Under a book cover without a Kindle link"],
    ["book.open_series", "Open the Chronicle", "Button beside each series on the home page"],
    ["series.eyebrow", "A Chronicle of the Imprint", "Small heading on a series page"],
    ["series.lore_eyebrow", "Within these pages", "Small heading above the series' lore pages"],
    ["series.lore_title", "Lore of the Chronicle", "Series lore heading"],
  ]),
  g("codex", "Sections, codex & map", false, [
    ["section.eyebrow", "A chamber of the codex", "Small heading on every section page"],
    ["codex.eyebrow", "The complete archive", "Small heading on All Pages"],
    ["codex.title", "The Codex, A to Z", "All Pages heading"],
    ["map.eyebrow", "Cartography of the realms", "Small heading on the map page"],
    ["map.guide", "Guide", "Small heading above each kingdom list"],
    ["map.enlarge", "Enlarge", "Button on the map image"],
  ]),
  g("article", "Article pages", false, [
    ["article.contributed", "Contributed by", "Before a reader's name"],
    ["article.gallery_eyebrow", "Illuminations", "Small heading above the gallery"],
    ["article.gallery_title", "Gallery", "Gallery heading"],
    ["article.tree_eyebrow", "Bloodline", "Small heading above a family tree"],
    ["article.tree_title", "Family Tree", "Family tree heading"],
    ["article.appears", "Appears in", "Books and series list heading"],
    ["article.backlinks", "Pages that link here", "Backlinks heading"],
    ["article.more_from", "More from", "Before the section name at the bottom"],
    ["article.reveal_all", "Reveal all", "Spoiler notice button"],
    ["article.hide_spoilers", "Hide spoilers", "Spoiler notice button (after revealing)"],
    ["shield.eyebrow", "Spoiler Shield", "Small heading in the spoiler panel"],
    ["shield.title", "How far have you read?", "Spoiler panel heading"],
  ]),
  g("readers", "Reader pages", false, [
    ["join.eyebrow", "The Readers' Scriptorium", "Small heading on the reader sign-up page"],
    ["join.title", "Add your ink to the chronicle", "Reader sign-up heading"],
    ["join.tab_join", "Become a contributor", "Sign-up tab"],
    ["join.tab_signin", "Sign in", "Sign-in tab"],
    ["join.create", "Create account", "Sign-up submit button"],
    ["scrolls.title", "My Scrolls", "Reader dashboard heading"],
    ["scrolls.new", "Write a new article", "Reader dashboard button"],
    ["scrolls.profile", "My profile", "Reader dashboard button"],
    ["scrolls.sign_out", "Sign out", "Reader dashboard button"],
    ["profile.eyebrow", "Reader of the Codex", "Small heading on a reader profile"],
    ["profile.list_eyebrow", "Scrolls in the Readers' Chronicles", "Small heading above contributions"],
    ["profile.list_title", "Approved contributions", "Contributions heading"],
    ["profile.edit", "Edit profile", "Profile edit button"],
  ]),
  g("newsletter", "Newsletter box & cookie notice", false, [
    ["newsletter.eyebrow", "Book release news", "Small heading on the newsletter box"],
    ["newsletter.title", "Be the first to know", "Newsletter box heading"],
    ["newsletter.text", "Get an email when a new book in the chronicle is released. No spam, unsubscribe any time.", "Newsletter box text", 300],
    ["newsletter.button", "Sign me up", "Newsletter button"],
    ["newsletter.consent", "I agree to receive emails about new book releases.", "Newsletter consent checkbox", 300],
    ["newsletter.success", "Almost there! Check your inbox and click the link to confirm your subscription.", "Message after signing up", 300],
    ["cookie.text", "This site uses only essential cookies and browser storage to keep you signed in and remember your preferences. No tracking or advertising cookies.", "Cookie notice text (shown when legal pages are on)", 300],
    ["cookie.link", "Privacy Notice", "Cookie notice link"],
    ["cookie.ok", "OK", "Cookie notice button"],
  ]),
  g("footer", "Footer & not-found page", false, [
    ["footer.codex", "The Codex", "Footer column heading"],
    ["footer.wander", "Wander", "Footer column heading"],
    ["footer.map", "Map of the Realms", "Footer link"],
    ["footer.all_pages", "All Pages A–Z", "Footer link"],
    ["notfound.eyebrow", "Lost in the fog", "Small heading on missing pages"],
    ["notfound.title", "This page is unwritten", "Missing page heading"],
    ["notfound.button", "Return to the Hall", "Missing page button"],
  ]),
  g("core-nav", "Main menu (top of every page)", true, [
    ["nav.chronicles", "Chronicles", "Top menu"],
    ["nav.codex", "Codex", "Top menu"],
    ["nav.all_pages", "All Pages A–Z", "Inside the Codex menu"],
    ["nav.map", "Map", "Top menu"],
    ["nav.vault", "Vault", "Top menu (when the Vault is switched on)"],
    ["nav.wander", "Wander", "Top menu random-page button"],
    ["nav.more", "More", "Top menu dropdown holding your custom buttons (only shown when you add some)"],
    ["nav.reader_contribution", "Reader Contribution", "Red button for visitors"],
    ["nav.my_scrolls", "My Scrolls", "Account button for signed-in readers"],
    ["nav.study", "Study", "Account button when you are signed in as admin"],
    ["nav.search", "Search the codex…", "Search box hint"],
  ]),
  g("core-mobile", "Slide-out menu (phones & tablets)", true, [
    ["mobile.title", "The Codex", "Menu heading"],
    ["mobile.wander", "Wander the Codex", "Random-page button"],
    ["mobile.map", "Map of the Realms", "Menu link"],
    ["mobile.all_pages", "All Pages", "Menu link"],
    ["mobile.vault", "The Royal Vault", "Menu link"],
    ["mobile.study", "Lorekeeper's Study (admin)", "Menu link when signed in as admin"],
    ["mobile.scrolls", "My Scrolls", "Menu link for signed-in readers"],
    ["mobile.join", "Reader Login / Sign up", "Menu link for visitors"],
    ["mobile.profile", "My Reader Profile", "Menu link for signed-in readers"],
  ]),
  g("core-admin", "Admin side panel", true, [
    ["admin.side.title", "Lorekeeper's Study", "Name beside the logo"],
    ["admin.side.dashboard", "Dashboard", "Side panel link"],
    ["admin.side.articles", "Articles", "Side panel link"],
    ["admin.side.submissions", "Reader Submissions", "Side panel link"],
    ["admin.side.links", "Lore Links", "Side panel link"],
    ["admin.side.templates", "Article Templates", "Side panel link"],
    ["admin.side.sections", "Sections", "Side panel link"],
    ["admin.side.series", "Series & Books", "Side panel link"],
    ["admin.side.vault", "Royal Vault", "Side panel link"],
    ["admin.side.users", "Readers & Accounts", "Side panel link"],
    ["admin.side.security", "Account & Security", "Side panel link"],
    ["admin.side.newsletter", "Newsletter", "Side panel link"],
    ["admin.side.wording", "Site Wording", "Side panel link"],
    ["admin.side.custom", "Custom Buttons & Headings", "Side panel link"],
    ["admin.side.settings", "Settings", "Side panel link"],
    ["admin.side.view_site", "View site", "Side panel link"],
    ["admin.side.sign_out", "Sign out", "Side panel button"],
  ]),
  g("core-login", "Sign-in screens", true, [
    ["login.eyebrow", "The Lorekeeper's Study", "Small heading on the login choice screen"],
    ["login.title", "Enter the Scriptorium", "Login screen heading"],
    ["login.admin_choice", "Admin Login", "Admin choice button"],
    ["login.reader_choice", "Reader Contribution Login", "Reader choice button"],
  ]),
];

export const DEFAULT_LABELS = Object.fromEntries(WORDING_GROUPS.flatMap((grp) => grp.items.map((i) => [i.key, i.def])));

// Items that can never be hidden, so nobody gets locked out
const LOCKED_PREFIXES = ["admin.side.", "login."];
const LOCKED = new Set(["nav.reader_contribution", "nav.more", "nav.my_scrolls", "nav.study", "nav.search", "mobile.title", "mobile.join", "mobile.scrolls", "mobile.study",
  "footer.admin_link", "join.tab_join", "join.tab_signin", "join.create", "scrolls.new", "scrolls.sign_out", "profile.edit", "article.contributed",
  "article.more_from", "article.hide_spoilers", "newsletter.title", "newsletter.button", "newsletter.consent", "newsletter.success",
  "cookie.text", "cookie.link", "cookie.ok"]);
export const canHide = (k) => !LOCKED.has(k) && !LOCKED_PREFIXES.some((p) => k.startsWith(p));

export const useT = () => {
  const { settings } = useSite();
  const custom = settings.labels || {};
  const hidden = new Set(settings.hidden_labels || []);
  return (k) => (hidden.has(k) && canHide(k) ? "" : (custom[k] || "").trim() || DEFAULT_LABELS[k] || k);
};
